import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

type UserRole = "admin" | "matchmaker" | "user" | null;

const PRIORITY: Record<Exclude<UserRole, null>, number> = {
  admin: 3,
  matchmaker: 2,
  user: 1,
};

const cacheKey = (userId: string) => `mas:userRole:${userId}`;

const readCachedRole = (userId: string | undefined): UserRole => {
  if (!userId || typeof window === "undefined") return null;
  const cached = window.sessionStorage.getItem(cacheKey(userId));
  if (cached === "admin" || cached === "matchmaker" || cached === "user") {
    return cached;
  }
  return null;
};

export function useUserRole() {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole>(() => readCachedRole(user?.id));
  const [loading, setLoading] = useState(() => readCachedRole(user?.id) === null);

  useEffect(() => {
    const checkUserRole = async () => {
      if (!user) {
        setRole(null);
        setLoading(false);
        return;
      }

      const cached = readCachedRole(user.id);
      if (cached) {
        setRole(cached);
        setLoading(false);
      }

      try {
        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id);

        if (error) throw error;

        const rows = (data ?? []) as { role: Exclude<UserRole, null> }[];
        const top = rows
          .map((r) => r.role)
          .sort((a, b) => PRIORITY[b] - PRIORITY[a])[0];

        const resolved = top ?? "user";
        setRole(resolved);
        if (typeof window !== "undefined") {
          window.sessionStorage.setItem(cacheKey(user.id), resolved);
        }
      } catch (error) {
        console.error("auth.user_role_check_failed");
        setRole("user");
      } finally {
        setLoading(false);
      }
    };

    checkUserRole();
  }, [user]);

  return {
    role,
    loading,
    isAdmin: role === "admin",
    isMatchmaker: role === "matchmaker",
    isUser: role === "user",
  };
}
